import React, { useState, useEffect, ReactNode, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import SEO from './components/SEO';
import PWAInstallBanner from './components/PWAInstallBanner';
import ScrollToTopButton from './components/ScrollToTop';
// Eager: only the homepage (the highest-traffic entry point) stays in the main bundle.
import HomePage from './pages/HomePage';
import { ThemeMode } from './types';
import { lazyWithPreload, PreloadableComponent } from './lazyWithPreload';
// Lazy: every other route is split into its own chunk so the main bundle no longer
// ships symbols.ts (~165KB), blogPosts.ts (~103KB), styleContent.ts (~260KB) or the
// 50+ page components. We use lazyWithPreload (not React.lazy) so the matched route's
// chunk can be preloaded before hydrateRoot — see preloadRouteForPath + src/index.tsx —
// which keeps the route split AND avoids the Suspense-fallback hydration mismatch with
// react-snap's prerendered HTML.
const StylePage = lazyWithPreload(() => import('./pages/StylePage'));
const PrivacyPolicy = lazyWithPreload(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyWithPreload(() => import('./pages/TermsOfService'));
const AboutPage = lazyWithPreload(() => import('./pages/AboutPage'));
const AppPage = lazyWithPreload(() => import('./pages/AppPage'));
const InstagramFontsPage = lazyWithPreload(() => import('./pages/InstagramFontsPage'));
const DiscordFontsPage = lazyWithPreload(() => import('./pages/DiscordFontsPage'));
const BoldGeneratorPage = lazyWithPreload(() => import('./pages/BoldGeneratorPage'));
const AestheticFontsPage = lazyWithPreload(() => import('./pages/AestheticFontsPage'));
const FacebookFontsPage = lazyWithPreload(() => import('./pages/FacebookFontsPage'));
const TinyTextPage = lazyWithPreload(() => import('./pages/TinyTextPage'));
const OldEnglishPage = lazyWithPreload(() => import('./pages/OldEnglishPage'));
const CoolFontsPage = lazyWithPreload(() => import('./pages/CoolFontsPage'));
const StylishNameGeneratorPage = lazyWithPreload(() => import('./pages/StylishNameGeneratorPage'));
const JawaeseTranslatorPage = lazyWithPreload(() => import('./pages/JawaeseTranslatorPage'));
const NumberToWordsPage = lazyWithPreload(() => import('./pages/NumberToWordsPage'));
const FancyLetterGeneratorPage = lazyWithPreload(() => import('./pages/FancyLetterGeneratorPage'));
const StylishTextPage = lazyWithPreload(() => import('./pages/StylishTextPage'));
const CuteNameGeneratorPage = lazyWithPreload(() => import('./pages/CuteNameGeneratorPage'));
const FancyNumbersPage = lazyWithPreload(() => import('./pages/FancyNumbersPage'));
const SciFiGeneratorPage = lazyWithPreload(() => import('./pages/SciFiGeneratorPage'));
const BirthdayFontsPage = lazyWithPreload(() => import('./pages/BirthdayFontsPage'));
const GamingFontsPage = lazyWithPreload(() => import('./pages/GamingFontsPage'));
const TimesNewRomanFontPage = lazyWithPreload(() => import('./pages/TimesNewRomanFontPage'));
const ElegantFontsPage = lazyWithPreload(() => import('./pages/ElegantFontsPage'));
const TwitterFontsPage = lazyWithPreload(() => import('./pages/TwitterFontsPage'));
const FantasyFontsPage = lazyWithPreload(() => import('./pages/FantasyFontsPage'));
const FuturisticFontGeneratorPage = lazyWithPreload(() => import('./pages/FuturisticFontGeneratorPage'));
const LeetTranslatorPage = lazyWithPreload(() => import('./pages/LeetTranslatorPage'));
const FancyTextPage = lazyWithPreload(() => import('./pages/FancyTextPage'));
const StylishTextConverterPage = lazyWithPreload(() => import('./pages/StylishTextConverterPage'));
const FancyLettersPage = lazyWithPreload(() => import('./pages/FancyLettersPage'));
const FancyCopyPastePage = lazyWithPreload(() => import('./pages/FancyCopyPastePage'));
const CopyPasteFontsPage = lazyWithPreload(() => import('./pages/CopyPasteFontsPage'));
const SpanishPage = lazyWithPreload(() => import('./pages/SpanishPage'));
const PortuguesePage = lazyWithPreload(() => import('./pages/PortuguesePage'));
const FrenchPage = lazyWithPreload(() => import('./pages/FrenchPage'));
const IndonesianPage = lazyWithPreload(() => import('./pages/IndonesianPage'));
const FilipinoPage = lazyWithPreload(() => import('./pages/FilipinoPage'));
const JapanesePage = lazyWithPreload(() => import('./pages/JapanesePage'));
const KoreanPage = lazyWithPreload(() => import('./pages/KoreanPage'));
const ChinesePage = lazyWithPreload(() => import('./pages/ChinesePage'));
const GermanPage = lazyWithPreload(() => import('./pages/GermanPage'));
const ThaiPage = lazyWithPreload(() => import('./pages/ThaiPage'));
const VietnamesePage = lazyWithPreload(() => import('./pages/VietnamesePage'));
const ContactPage = lazyWithPreload(() => import('./pages/ContactPage'));
const TextAnalyzerPage = lazyWithPreload(() => import('./pages/TextAnalyzerPage'));
const CaseConverterPage = lazyWithPreload(() => import('./pages/CaseConverterPage'));
const SymbolsPage = lazyWithPreload(() => import('./pages/SymbolsPage'));
const BlogPage = lazyWithPreload(() => import('./pages/BlogPage'));
const BlogPostPage = lazyWithPreload(() => import('./pages/BlogPostPage'));
const FAQPage = lazyWithPreload(() => import('./pages/FAQPage'));
const CompatibilityPage = lazyWithPreload(() => import('./pages/CompatibilityPage'));
const FontChangerPage = lazyWithPreload(() => import('./pages/FontChangerPage'));
const CoolTextPage = lazyWithPreload(() => import('./pages/CoolTextPage'));
const FontOnlinePage = lazyWithPreload(() => import('./pages/FontOnlinePage'));
const TextStylesPage = lazyWithPreload(() => import('./pages/TextStylesPage'));
const FontKeyboardPage = lazyWithPreload(() => import('./pages/FontKeyboardPage'));

// Maps a pathname to the lazy route chunk that renders it, so src/index.tsx can
// preload the matched chunk before hydrateRoot — keeping every route split while
// avoiding the Suspense-fallback hydration mismatch against react-snap's prerendered HTML.
const ROUTE_PRELOAD: Record<string, PreloadableComponent> = {
  '/privacy': PrivacyPolicy,
  '/terms': TermsOfService,
  '/about': AboutPage,
  '/app': AppPage,
  '/instagram-fonts': InstagramFontsPage,
  '/discord-fonts': DiscordFontsPage,
  '/facebook-fonts': FacebookFontsPage,
  '/bold-generator': BoldGeneratorPage,
  '/aesthetic-fonts': AestheticFontsPage,
  '/tiny-text': TinyTextPage,
  '/old-english': OldEnglishPage,
  '/cool-fonts': CoolFontsPage,
  '/stylish-name-generator': StylishNameGeneratorPage,
  '/jawa-translator': JawaeseTranslatorPage,
  '/number-to-words': NumberToWordsPage,
  '/fancy-letter-generator': FancyLetterGeneratorPage,
  '/stylish-text': StylishTextPage,
  '/cute-name-generator': CuteNameGeneratorPage,
  '/fancy-numbers': FancyNumbersPage,
  '/scifi-fonts': SciFiGeneratorPage,
  '/birthday-fonts': BirthdayFontsPage,
  '/gaming-fonts': GamingFontsPage,
  '/times-new-roman-font': TimesNewRomanFontPage,
  '/elegant-fonts': ElegantFontsPage,
  '/twitter-fonts': TwitterFontsPage,
  '/fantasy-fonts': FantasyFontsPage,
  '/futuristic-font-generator': FuturisticFontGeneratorPage,
  '/leet-translator': LeetTranslatorPage,
  '/fancy-text': FancyTextPage,
  '/stylish-text-converter': StylishTextConverterPage,
  '/fancy-letters': FancyLettersPage,
  '/fancy-copy-paste': FancyCopyPastePage,
  '/copy-paste-fonts': CopyPasteFontsPage,
  '/contact': ContactPage,
  '/text-analyzer': TextAnalyzerPage,
  '/case-converter': CaseConverterPage,
  '/symbols': SymbolsPage,
  '/faq': FAQPage,
  '/compatibility': CompatibilityPage,
  '/font-changer': FontChangerPage,
  '/cool-text': CoolTextPage,
  '/font-online': FontOnlinePage,
  '/text-styles': TextStylesPage,
  '/font-keyboard': FontKeyboardPage,
  '/blog': BlogPage,
  '/es': SpanishPage,
  '/pt': PortuguesePage,
  '/fr': FrenchPage,
  '/id': IndonesianPage,
  '/ph': FilipinoPage,
  '/jp': JapanesePage,
  '/kr': KoreanPage,
  '/cn': ChinesePage,
  '/de': GermanPage,
  '/th': ThaiPage,
  '/vi': VietnamesePage,
};

/** Preload the lazy chunk that will render `pathname` (resolves immediately for the eager homepage). */
export function preloadRouteForPath(pathname: string): Promise<unknown> {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/') return Promise.resolve(); // HomePage is eager
  const exact = ROUTE_PRELOAD[p];
  if (exact) return exact.preload();
  if (p.startsWith('/blog/')) return BlogPostPage.preload();
  // Single-segment unknown path -> the dynamic /:styleSlug route.
  if (/^\/[^/]+$/.test(p)) return StylePage.preload();
  return Promise.resolve();
}

// Scroll to top on route change
function ScrollToTop(): React.ReactElement | null {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Main App Layout Props
interface AppLayoutProps {
  children: ReactNode;
  theme: ThemeMode;
  isDarkMode: boolean;
  onCycleTheme: () => void;
}

// Main App Layout
function AppLayout({ children, theme, isDarkMode, onCycleTheme }: AppLayoutProps): React.ReactElement {
  return (
    <div className="app">
      <SEO />
      <Header theme={theme} isDarkMode={isDarkMode} onCycleTheme={onCycleTheme} />
      <main className="main-content">
        {children}
      </main>
      <Footer />
      <PWAInstallBanner />
      <ScrollToTopButton />
    </div>
  );
}

// react-snap snapshots a client-rendered DOM, which has NO React Suspense hydration
// markers (<!--$-->). Hydrating a <Suspense>-wrapped tree against marker-less HTML
// mismatches before the route content on every page (React #418/#423). So for the
// initial hydration of a prerendered page we render the routes WITHOUT the Suspense
// wrapper (index.tsx preloads the matched chunk first, so it won't suspend), then add
// Suspense back after mount for client-side navigation to other lazy routes. During
// react-snap and on non-prerendered createRoot loads we keep Suspense from the start.
function shouldUseSuspenseInitially(): boolean {
  if (typeof document === 'undefined' || typeof navigator === 'undefined') return true;
  const hasPrerenderedRoot = !!document.getElementById('root')?.hasChildNodes();
  const isReactSnap = navigator.userAgent.includes('ReactSnap');
  return isReactSnap || !hasPrerenderedRoot;
}

function App(): React.ReactElement {
  // Initialize with static defaults to match server-rendered HTML and avoid hydration mismatch.
  // Default is LIGHT for everyone (not 'auto'): visitors only get dark mode if they
  // pick it with the toggle. Must match the prerendered HTML (react-snap uses this too).
  const [theme, setTheme] = useState<ThemeMode>('light');
  const [systemDark, setSystemDark] = useState<boolean>(false);
  const [isClient, setIsClient] = useState(false);
  const [useRouteSuspense, setUseRouteSuspense] = useState<boolean>(shouldUseSuspenseInitially);

  // After the initial (marker-less) hydration commits, enable the Suspense boundary
  // so client-side navigation to not-yet-loaded lazy routes shows the fallback.
  useEffect(() => {
    if (!useRouteSuspense) setUseRouteSuspense(true);
  }, [useRouteSuspense]);

  // This effect runs once on the client after initial hydration.
  useEffect(() => {
    // Skip during react-snap prerendering: puppeteer runs in dark mode, so detecting
    // the system theme here would bake <body class="dark-mode"> into every static page
    // and flash dark→light for real (mostly light-mode) visitors. Let the real client
    // detect the theme instead; the prerendered HTML stays theme-neutral.
    if (navigator.userAgent.includes('ReactSnap')) {
      return;
    }

    // Set a flag to indicate we're on the client.
    setIsClient(true);

    // Safely access client-side APIs.
    // Only honor a theme the visitor explicitly chose with the toggle ('themePreference').
    // The legacy 'theme' key was auto-saved as 'auto' for every visitor, so a legacy
    // 'auto' is ignored (-> light); a legacy 'light'/'dark' was an explicit choice.
    const savedPref = localStorage.getItem('themePreference');
    const legacy = localStorage.getItem('theme');
    if (savedPref && ['auto', 'light', 'dark'].includes(savedPref)) {
      setTheme(savedPref as ThemeMode);
    } else if (legacy === 'light' || legacy === 'dark') {
      setTheme(legacy);
      localStorage.setItem('themePreference', legacy);
    }
    localStorage.removeItem('theme');

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemDark(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent): void => setSystemDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Determine if dark mode should be active
  const isDarkMode = theme === 'dark' || (theme === 'auto' && systemDark);

  // Apply dark mode class to body and save preference - only on client
  useEffect(() => {
    if (isClient) {
      document.body.classList.toggle('dark-mode', isDarkMode);

      // Update theme-color meta tag for PWA
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', isDarkMode ? '#0f172a' : '#0D9488');
      }
    }
  }, [isDarkMode, theme, isClient]);

  // Cycle through themes: light -> dark -> auto -> light. Only explicit choices are saved.
  const cycleTheme = (): void => {
    setTheme(current => {
      const next: ThemeMode = current === 'light' ? 'dark' : current === 'dark' ? 'auto' : 'light';
      try { localStorage.setItem('themePreference', next); } catch { /* storage unavailable */ }
      return next;
    });
  };

  const routesEl = (
    <Routes>
          {/* Main Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/app" element={<AppPage />} />

          {/* Platform-Specific Landing Pages */}
          <Route path="/instagram-fonts" element={<InstagramFontsPage />} />
          <Route path="/discord-fonts" element={<DiscordFontsPage />} />
          <Route path="/facebook-fonts" element={<FacebookFontsPage />} />

          {/* SEO Keyword Landing Pages */}
          <Route path="/bold-generator" element={<BoldGeneratorPage />} />
          <Route path="/aesthetic-fonts" element={<AestheticFontsPage />} />
          <Route path="/tiny-text" element={<TinyTextPage />} />
          <Route path="/old-english" element={<OldEnglishPage />} />
          <Route path="/cool-fonts" element={<CoolFontsPage />} />
          <Route path="/stylish-name-generator" element={<StylishNameGeneratorPage />} />
          <Route path="/jawa-translator" element={<JawaeseTranslatorPage />} />
          <Route path="/number-to-words" element={<NumberToWordsPage />} />
          <Route path="/fancy-letter-generator" element={<FancyLetterGeneratorPage />} />
          <Route path="/stylish-text" element={<StylishTextPage />} />
          <Route path="/cute-name-generator" element={<CuteNameGeneratorPage />} />
          <Route path="/fancy-numbers" element={<FancyNumbersPage />} />
          <Route path="/scifi-fonts" element={<SciFiGeneratorPage />} />
          <Route path="/birthday-fonts" element={<BirthdayFontsPage />} />
          <Route path="/gaming-fonts" element={<GamingFontsPage />} />
          <Route path="/times-new-roman-font" element={<TimesNewRomanFontPage />} />
          <Route path="/elegant-fonts" element={<ElegantFontsPage />} />
          <Route path="/twitter-fonts" element={<TwitterFontsPage />} />
          <Route path="/fantasy-fonts" element={<FantasyFontsPage />} />
          <Route path="/futuristic-font-generator" element={<FuturisticFontGeneratorPage />} />
          <Route path="/leet-translator" element={<LeetTranslatorPage />} />
          <Route path="/fancy-text" element={<FancyTextPage />} />
          <Route path="/stylish-text-converter" element={<StylishTextConverterPage />} />
          <Route path="/fancy-letters" element={<FancyLettersPage />} />
          <Route path="/fancy-copy-paste" element={<FancyCopyPastePage />} />
          <Route path="/copy-paste-fonts" element={<CopyPasteFontsPage />} />
          <Route path="/font-changer" element={<FontChangerPage />} />
          <Route path="/cool-text" element={<CoolTextPage />} />
          <Route path="/font-online" element={<FontOnlinePage />} />
          <Route path="/text-styles" element={<TextStylesPage />} />
          <Route path="/font-keyboard" element={<FontKeyboardPage />} />

          {/* Redirects for SEO aliases */}
          <Route path="/ig-fonts" element={<Navigate to="/instagram-fonts" replace />} />
          <Route path="/cursive-letters" element={<Navigate to="/cursive" replace />} />

          {/* Tools */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/text-analyzer" element={<TextAnalyzerPage />} />
          <Route path="/case-converter" element={<CaseConverterPage />} />
          <Route path="/symbols" element={<SymbolsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/compatibility" element={<CompatibilityPage />} />

          {/* Blog */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Multilingual Pages */}
          <Route path="/es" element={<SpanishPage />} />
          <Route path="/pt" element={<PortuguesePage />} />
          <Route path="/fr" element={<FrenchPage />} />
          <Route path="/id" element={<IndonesianPage />} />
          <Route path="/ph" element={<FilipinoPage />} />
          <Route path="/jp" element={<JapanesePage />} />
          <Route path="/kr" element={<KoreanPage />} />
          <Route path="/cn" element={<ChinesePage />} />
          <Route path="/de" element={<GermanPage />} />
          <Route path="/th" element={<ThaiPage />} />
          <Route path="/vi" element={<VietnamesePage />} />

          {/* Individual Style Pages - Dynamic Route */}
          <Route path="/:styleSlug" element={<StylePage />} />
    </Routes>
  );

  return (
    <Router>
      <ScrollToTop />
      <AppLayout theme={theme} isDarkMode={isDarkMode} onCycleTheme={cycleTheme}>
        {useRouteSuspense ? (
          <Suspense fallback={<div className="route-loading" aria-busy="true" />}>
            {routesEl}
          </Suspense>
        ) : (
          routesEl
        )}
      </AppLayout>
    </Router>
  );
}

export default App;
